// src/components/TopicMafia/TopicMafiaRoom.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import FinalResultDialog from "./components/FinalResultDialog";
import GuessDialog from "./components/GuessDialog";
import VoteDialog from "./components/VoteDialog";
import ForceEndDialog from "./components/ForceEndDialog";
import VoteResultDialog from "./components/VoteResultDialog";


import {
    Alert,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Divider,
    MenuItem,
    Paper,
    Slide,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    useMediaQuery,
    useTheme,

} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";

import useTopicMafiaRoomApi from "./hooks/useTopicMafiaRoomApi";
import useTopicMafiaWS from "./hooks/useTopicMafiaWS";

import {
    STATUS_COLOR,
    STATUS_LABEL,
    cardSx,
    controlBtnSx,
    controlFieldSx,
    mainActionBtnSx,
    sectionTitleSx,
    subActionBtnSx,
} from "./topicMafiaUi";

/**
 * ✅ 모바일 UX 개선(완성본)
 * - 모바일: 하단 탭(게임/타임라인/호스트)로 한 번에 한 섹션만 보여줌
 * - 타임라인: 모바일 글자/행간 개선 + 시간 HH:mm
 * - 게임 진행 sticky bar: 모바일에서는 sticky 해제
 * - READY 하단바: 불필요 문구 제거, 버튼만 "준비/준비완료"
 */
export default function TopicMafiaRoom() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const { roomId } = useParams();
    const navigate = useNavigate();



    const [confirmStartOpen, setConfirmStartOpen] = useState(false);
    const timelineRef = useRef(null);
    const [sayCloseConfirmOpen, setSayCloseConfirmOpen] = useState(false);

    // ✅ 모바일 섹션 탭
    const [mobileTab, setMobileTab] = useState("game"); // game | timeline | host

    // vote ui
    const [voteOpen, setVoteOpen] = useState(false);
    const [voteTargetId, setVoteTargetId] = useState(null);
    const [myVotedTargetId, setMyVotedTargetId] = useState(null);

    // vote countdown


    // turn countdown
    const [turnEndsAt, setTurnEndsAt] = useState(null);



    // now tick
    const [nowTs, setNowTs] = useState(Date.now());

    // force end
    const [forceEndOpen, setForceEndOpen] = useState(false);
    const [forceEndReason, setForceEndReason] = useState("");

    // vote result UI
    const [voteResultDialogOpen, setVoteResultDialogOpen] = useState(false);
    const [voteResultPayload, setVoteResultPayload] = useState(null);

    // vote result snack
    const [voteSnackOpen, setVoteSnackOpen] = useState(false);
    const [voteSnackMsg, setVoteSnackMsg] = useState("");

    // guess ui
    const [guessOpen, setGuessOpen] = useState(false);
    const [guessText, setGuessText] = useState("");
    const [guessEndsAt, setGuessEndsAt] = useState(null);

    // host settings save snack
    const [hostSaveSnackOpen, setHostSaveSnackOpen] = useState(false);
    const [hostSaveSnackMsg, setHostSaveSnackMsg] = useState("");
    const hostSettingsOrigRef = useRef(null);
    const hostSettingsDirtyRef = useRef(false);

    // ✅ VOTING 진입 후 5초 뒤 투표 오픈(모바일)
    const [voteRevealAt, setVoteRevealAt] = useState(null); // ms timestamp
    const openedVoteOnceRef = useRef(null); // 같은 VOTING에서 자동오픈 1회 가드

    // ✅ 5초 카운트다운(5..4..3..) 계산
    const voteDelayLeftSec = useMemo(() => {
        if (!voteRevealAt) return 0;
        return Math.max(0, Math.ceil((voteRevealAt - nowTs) / 1000));
    }, [voteRevealAt, nowTs]);

    const voteDelayPassed = useMemo(() => {
        return voteRevealAt != null && voteDelayLeftSec === 0;
    }, [voteRevealAt, voteDelayLeftSec]);
    // ✅ revealAt 기반 지연이 "적용되는 상황"인지 (모바일에서만 적용)
    const voteDelayActive = useMemo(() => {
        return isMobile && !!voteRevealAt && !voteDelayPassed;
    }, [isMobile, voteRevealAt, voteDelayPassed]);

    const {
        state,
        setState,
        says,
        setSays,
        error,

        room,
        players,
        me,
        joined,
        isHost,
        allReady,
        myReady,
        myPlayerId,

        hostSettings,
        setHostSettings,
        savingHostSettings,
        saveHostSettings,
        topicPairs,
        topicPairsLoading,
        fetchTopicPairs,

        fetchState,
        postJoin,
        postLeave,
        postStart,
        postSay,
        postVote,
        postForceEnd,
        postToggleReady,
        postGuess,
        postKick, // ✅ 추가
        postRematch,
    } = useTopicMafiaRoomApi(roomId);


    // ✅ 여기로 내려
    const voteEndsAtServer = room?.vote_ends_at;
    const turnEndsAtServer = room?.turn_ends_at;
    const guessEndsAtServer = room?.guess_ends_at;

    const safePlayers = useMemo(() => players || [], [players]);
    const safeSays = useMemo(() => says || [], [says]);
    const fetchedAfterVoteEndRef = useRef(false);
    const [errorSnackOpen, setErrorSnackOpen] = useState(false);
    const [errorSnackMsg, setErrorSnackMsg] = useState("");

    // ✅ 모바일 내 차례 입력 모달
    const [sayDialogOpen, setSayDialogOpen] = useState(false);
    const lastOpenedTurnKeyRef = useRef(null);

    const currentPlayerId = state?.current_player_id;
    const myTopic = me?.my_topic ?? null;

    // ✅ GUESS 자동 오픈 1회 제어
    const openedGuessOnceRef = useRef(false);

    // ✅ myTopic 없을 때 개인화 state 1회 당겨오기
    const pulledMyTopicOnceRef = useRef(false);

    // ✅ 최근 라운드 최다 득표 결과(요약)
    const [lastVoteSummary, setLastVoteSummary] = useState(null);

    const roomRound = room?.round ?? 0;



    // ✅ 새로고침해도 유지 (옵션이지만 강추)
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem(`tm_lastVote_${roomId}`);
            if (raw) setLastVoteSummary(JSON.parse(raw));
        } catch (e) { }
    }, [roomId]);

    useEffect(() => {
        try {
            if (!lastVoteSummary) return;
            sessionStorage.setItem(`tm_lastVote_${roomId}`, JSON.stringify(lastVoteSummary));
        } catch (e) { }
    }, [roomId, lastVoteSummary]);

    // ✅ hostSettings 들어오면 orig 갱신(사용자 수정 중이면 유지)
    useEffect(() => {
        if (!hostSettings) return;
        const incoming = JSON.stringify(hostSettings);

        if (!hostSettingsOrigRef.current) {
            hostSettingsOrigRef.current = incoming;
            return;
        }
        if (hostSettingsDirtyRef.current) return;

        hostSettingsOrigRef.current = incoming;
    }, [hostSettings]);



    useEffect(() => {
        if (room?.status === "WAITING" || room?.status === "ENDED" || room?.status === "REVEAL") {
            pulledMyTopicOnceRef.current = false;
        }
    }, [room?.status]);

    const statusLabel = room?.status ? STATUS_LABEL[room.status] : "로딩";
    const statusColor = room?.status ? STATUS_COLOR[room.status] : "default";

    const alivePlayers = useMemo(() => safePlayers.filter((p) => p.is_alive), [safePlayers]);

    const playerNameMap = useMemo(() => {
        const m = new Map();
        safePlayers.forEach((p) => {
            const name = p.nickname || p.username || `#${p.id}`;
            m.set(Number(p.id), name);
        });
        return m;
    }, [safePlayers]);

    // ✅ host만 WAITING에서 강퇴 가능 
    const canKick = isHost && room?.status === "WAITING" && joined;

    // ✅ 강퇴 모드(멀티 선택) 
    const [kickMode, setKickMode] = useState(false);
    const [kickSelectedIds, setKickSelectedIds] = useState([]); // number[] 
    const toggleKickSelect = useCallback((playerId) => {
        const id = Number(playerId);
        setKickSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    }, []);

    // ✅ 다중 강퇴 확인 다이얼로그 
    const [kickConfirmOpen, setKickConfirmOpen] = useState(false);
    const selectedKickNames = useMemo(() => {
        return (kickSelectedIds || [])
            .map((id) => playerNameMap.get(Number(id)) || `#${id}`)
            .join(", ");
    }, [kickSelectedIds, playerNameMap]);

    const isMyTurn =
        room?.status === "PLAYING" &&
        myPlayerId != null &&
        currentPlayerId != null &&
        Number(currentPlayerId) === Number(myPlayerId);

    // ✅ 현재 차례인 사람 이름 (모바일/PC 공용)
    const currentTurnName = useMemo(() => {
        if (room?.status !== "PLAYING") return null;
        if (!currentPlayerId) return null;
        return playerNameMap.get(Number(currentPlayerId)) || `#${currentPlayerId}`;
    }, [room?.status, currentPlayerId, playerNameMap]);

    const voteCandidates = useMemo(
        () => alivePlayers.filter((p) => Number(p.id) !== Number(myPlayerId)),
        [alivePlayers, myPlayerId]
    );

    // ✅ remaining time helpers
    const voteRemainingSec = useMemo(() => {
        if (!voteEndsAtServer) return null;
        const diff = dayjs(voteEndsAtServer).diff(dayjs(nowTs), "second");
        return Math.max(0, diff);
    }, [voteEndsAtServer, nowTs]);

    const voteRemainingLabel = useMemo(() => {
        if (voteRemainingSec == null) return "";
        const mm = String(Math.floor(voteRemainingSec / 60)).padStart(2, "0");
        const ss = String(voteRemainingSec % 60).padStart(2, "0");
        return `${mm}:${ss}`;
    }, [voteRemainingSec]);

    const turnRemainingSec = useMemo(() => {
        if (!turnEndsAt) return null;
        const diff = dayjs(turnEndsAt).diff(dayjs(nowTs), "second");
        return Math.max(0, diff);
    }, [turnEndsAt, nowTs]);

    const turnRemainingLabel = useMemo(() => {
        if (turnRemainingSec == null) return "";
        const mm = String(Math.floor(turnRemainingSec / 60)).padStart(2, "0");
        const ss = String(turnRemainingSec % 60).padStart(2, "0");
        return `${mm}:${ss}`;
    }, [turnRemainingSec]);

    const guessRemainingSec = useMemo(() => {
        if (!guessEndsAt) return null;
        const diff = dayjs(guessEndsAt).diff(dayjs(nowTs), "second");
        return Math.max(0, diff);
    }, [guessEndsAt, nowTs]);

    const guessRemainingLabel = useMemo(() => {
        if (guessRemainingSec == null) return "";
        const mm = String(Math.floor(guessRemainingSec / 60)).padStart(2, "0");
        const ss = String(guessRemainingSec % 60).padStart(2, "0");
        return `${mm}:${ss}`;
    }, [guessRemainingSec]);

    // ✅ says grouping
    const saysGrouped = useMemo(() => {
        const sorted = safeSays
            .slice()
            .sort(
                (a, b) =>
                    (a.round ?? 0) - (b.round ?? 0) ||
                    (a.turn_index ?? 0) - (b.turn_index ?? 0) ||
                    dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf()
            );

        const map = new Map();
        for (const s of sorted) {
            const key = s.round ?? 0;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(s);
        }
        return Array.from(map.entries());
    }, [safeSays]);

    // ✅ Say 모달 하단에 보여줄 "최근 발언" (현재 라운드 우선)
    const recentSaysForDialog = useMemo(() => {
        if (!safeSays?.length) return [];

        const sorted = safeSays
            .slice()
            .sort((a, b) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf());

        const curRound = room?.round ?? null;

        // 1) 현재 라운드 발언이 있으면 그 라운드만(최근 12개)
        if (curRound != null) {
            const cur = sorted.filter((s) => Number(s.round ?? 0) === Number(curRound));
            if (cur.length > 0) return cur.slice(-12);
        }

        // 2) 없으면 전체에서 최근 12개
        return sorted.slice(-12);
    }, [safeSays, room?.round]);

    const canStart = isHost && room?.status === "WAITING" && joined && allReady;
    const selectedTopicId = hostSettings?.topic_category_id;
    const topicNotSelected = room?.status === "WAITING" && isHost && joined && !selectedTopicId;

    const startDisabledReason = useMemo(() => {
        if (!room) return "로딩 중…";
        if (!joined) return "먼저 참가해야 합니다.";
        if (room?.status !== "WAITING") return "대기 상태에서만 시작 가능.";
        if (!allReady) return "모든 참가자가 준비 완료여야 시작 가능.";
        return "";
    }, [room, joined, allReady]);

    const hostSettingsDirty = useMemo(() => {
        if (!hostSettingsOrigRef.current || !hostSettings) return false;
        return hostSettingsOrigRef.current !== JSON.stringify(hostSettings);
    }, [hostSettings]);

    // ✅ 강퇴 감지용: 과거에 참가한 적 있었는지 기록
    const wasJoinedRef = useRef(false);
    const kickedNavTimerRef = useRef(null);

    useEffect(() => {
        if (joined) wasJoinedRef.current = true;
    }, [joined]);

    // ✅ WAITING에서 "강퇴됨" 감지 → 스낵 띄우고 로비로 이동
    useEffect(() => {
        if (room?.status !== "WAITING") return;

        if (wasJoinedRef.current && !joined) {
            setErrorSnackMsg("호스트에 의해 퇴장되었습니다.");
            setErrorSnackOpen(true);

            setVoteOpen(false);
            setGuessOpen(false);
            setSayDialogOpen(false);
            setVoteResultDialogOpen(false);
            setKickConfirmOpen(false);

            if (kickedNavTimerRef.current) clearTimeout(kickedNavTimerRef.current);
            kickedNavTimerRef.current = setTimeout(() => {
                navigate("/main/games/topic-mafia");
            }, 700);
        }

        return () => {
            if (kickedNavTimerRef.current) clearTimeout(kickedNavTimerRef.current);
        };
    }, [room?.status, joined, navigate]);

    // ✅ host settings 저장
    const handleSaveHostSettings = useCallback(async () => {
        const res = await saveHostSettings(); // res가 undefined/boolean/object 모두 가능

        const ok = res === true || res?.ok === true;
        const eMsg = res?.error;

        if (!ok) {
            setHostSaveSnackMsg(eMsg || "설정 저장 실패");
            setHostSaveSnackOpen(true);
            return;
        }

        hostSettingsOrigRef.current = JSON.stringify(hostSettings);
        setHostSaveSnackMsg("✅ 설정이 저장되었습니다.");
        setHostSaveSnackOpen(true);
    }, [saveHostSettings, hostSettings]);

    // ✅ 시작 클릭 (dirty면 confirm 띄움)
    const handleStartClick = useCallback(() => {
        if (hostSettingsDirty) {
            setConfirmStartOpen(true);
            return;
        }
        postStart();
    }, [hostSettingsDirty, postStart]);

    // ✅ final dialog
    const [finalOpen, setFinalOpen] = useState(false);

    // ✅ turn warning
    const [turnWarnOpen, setTurnWarnOpen] = useState(false);
    const [turnWarnMsg, setTurnWarnMsg] = useState("");
    const warnedTurnKeyRef = useRef(null);

    // ✅ guess target
    const finalLoserPlayerId = room?.final_loser_player_id;
    const canGuess = useMemo(() => {
        if (!joined) return false;
        if (room?.status !== "GUESS") return false;
        if (!me?.player_id) return false;
        if (!finalLoserPlayerId) return false;
        return Number(me.player_id) === Number(finalLoserPlayerId);
    }, [joined, room?.status, me?.player_id, finalLoserPlayerId]);

    // ✅ 모바일: 내 차례 Say 모달 자동 오픈(턴마다 1회)
    useEffect(() => {
        if (!isMobile) return;

        // PLAYING 아니면 정리
        if (room?.status !== "PLAYING") {
            setSayDialogOpen(false);
            lastOpenedTurnKeyRef.current = null;
            return;
        }

        const turnKey = `${room?.round ?? 0}-${currentPlayerId ?? "x"}`;

        if (isMyTurn) {
            // 같은 턴에서 1회만 오픈
            if (lastOpenedTurnKeyRef.current !== turnKey) {
                lastOpenedTurnKeyRef.current = turnKey;

                // 내 턴이면 다른 모달/스낵이 방해되지 않게 정리
                setVoteResultDialogOpen(false);
                setVoteSnackOpen(false);
                setVoteOpen(false);

                setSayDialogOpen(true);
            }
        } else {
            setSayDialogOpen(false);
        }
    }, [isMobile, room?.status, room?.round, currentPlayerId, isMyTurn]);


    // ─────────────────────────────────────────────
    // WS: vote_result 처리
    // ─────────────────────────────────────────────
    const onVoteResult = useCallback(
        (p) => {
            const data = p?.payload ?? p;
            if (!data) return;

            const isTie = !!data.is_tie;
            const noVotes = !!data.no_votes;

            const topIds =
                data.top_player_ids ??
                data.loser_player_ids ??
                data.top_ids ??
                data.top_players ??
                (data.top_player_id != null ? [data.top_player_id] : []) ??
                data.top_user_ids ??
                (data.top_user_id != null ? [data.top_user_id] : []) ??
                [];

            const maxVotes = data.max_votes ?? null;

            let text = "🗳️ 투표 결과가 반영되었습니다.";

            if (noVotes) {
                text = "🗳️ 이번 라운드: 투표가 없어 최다 득표자 없음";
            } else if (topIds.length > 0) {
                const names = topIds
                    .map((id) => playerNameMap.get(Number(id)) || `#${id}`)
                    .join(", ");
                text = isTie
                    ? `🗳️ 이번 라운드 최다 득표(동점): ${names}${maxVotes != null ? ` (${maxVotes}표)` : ""}`
                    : `🗳️ 이번 라운드 최다 득표: ${names}${maxVotes != null ? ` (${maxVotes}표)` : ""}`;
            }

            // ✅ 최근 라운드 최다 득표자 저장
            setLastVoteSummary({
                at: Date.now(),
                round: data.round ?? roomRound ?? null,
                topIds: (topIds || []).map((x) => Number(x)),
                maxVotes: maxVotes ?? null,
                isTie,
                noVotes,
            });

            setVoteSnackMsg(text);
            setVoteSnackOpen(true);

            setVoteResultPayload(data);
            setVoteResultDialogOpen(true);

            setVoteResultPayload(data);
            setVoteResultDialogOpen(true);


            // ✅ vote_result 수신 후 다음 라운드/상태 반영을 위해 state 재조회(안전장치) 
            setTimeout(() => {
                fetchState();
            }, 150);
        },
        [playerNameMap, fetchState, roomRound]
    );

    useTopicMafiaWS({
        roomId,
        fetchState,
        setState,
        setSays,
        onVoteResult,
    });

    useEffect(() => {
        if (room?.status === "ENDED") setFinalOpen(true);
    }, [room?.status]);

    // ✅ (추가) 과반 조기 종료 포함: VOTING이 끝나면 투표창 닫기 + 선택 초기화
    useEffect(() => {
        if (!room?.status) return;

        if (room.status !== "VOTING") {
            setVoteOpen(false);
            setVoteTargetId(null);

            // (선택) 다음 투표 세션에서 초기화는 이미 따로 하고 있으니 굳이 건드릴 필요 X
            // setMyVotedTargetId(null);
            // setVoteRevealAt(null);
        }
    }, [room?.status]);


    // tick
    useEffect(() => {
        const t = setInterval(() => setNowTs(Date.now()), 250);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        if (room?.status !== "VOTING") return;
        if (voteRemainingSec === 0) {
            setVoteOpen(false);
            if (!fetchedAfterVoteEndRef.current) {
                fetchedAfterVoteEndRef.current = true;
                fetchState();
            }
        }
    }, [room?.status, voteRemainingSec, fetchState]);

    // ✅ 모바일: 5초 카운트 끝나면 투표 모달 자동 오픈
    useEffect(() => {
        if (!isMobile) return;
        if (room?.status !== "VOTING") return;
        if (!joined) return;
        if (myVotedTargetId) return;
        if (voteRemainingSec === 0) return;
        if (voteOpen) return;              // ✅ 추가: 이미 열려있으면 또 열지 마
        if (!voteRevealAt) return;          // ✅ 추가: revealAt 없으면 절대 열지 마
        if (!voteDelayPassed) return;

        // 같은 VOTING 세션에서 1회만 자동 오픈
        const key = voteEndsAtServer || `${room?.id ?? "x"}-${room?.round ?? 0}`;
        if (openedVoteOnceRef.current === key) return;

        openedVoteOnceRef.current = key;
        setVoteOpen(true);
    }, [
        isMobile,
        room?.status,
        room?.id,
        room?.round,

        roomRound,
        joined,
        myVotedTargetId,
        voteRemainingSec,
        voteDelayPassed,
        voteEndsAtServer,
        voteRevealAt,                        // ✅ deps 추가
        voteOpen,                 // ✅ deps

    ]);

    // ✅ VOTING 진입 "한번만" 리셋 가드
    const votingEnteredRef = useRef(false);

    useEffect(() => {
        if (room?.status !== "VOTING") {
            votingEnteredRef.current = false;
            return;
        }

        // ✅ 같은 VOTING 세션에서 1회만 리셋
        if (votingEnteredRef.current) return;
        votingEnteredRef.current = true;

        setMyVotedTargetId(null);
        setVoteTargetId(null);
        // setVoteOpen(false);  // ✅ 제거: 자동오픈/수동오픈 깜빡임 방지
    }, [room?.status]);

    useEffect(() => {
        if (!room?.status) return;

        if (room.status !== "VOTING") {
            setVoteRevealAt(null);
            openedVoteOnceRef.current = null;
            return;
        }

        const serverRevealMs = room?.vote_reveal_at
            ? dayjs(room.vote_reveal_at).valueOf()
            : null;

        setVoteRevealAt(serverRevealMs);
        openedVoteOnceRef.current = null;
    }, [room?.status, room?.vote_reveal_at]);

    // VOTING 들어가면 리셋 
    useEffect(() => {
        if (room?.status === "VOTING") fetchedAfterVoteEndRef.current = false;
    }, [room?.status]);

    useEffect(() => {
        if (!timelineRef.current) return;
        timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
    }, [safeSays.length]);

    // PLAYING timer
    useEffect(() => {
        if (!room?.status) return;
        if (room.status !== "PLAYING") {
            setTurnEndsAt(null);
            return;
        }
        setTurnEndsAt(turnEndsAtServer || null);
    }, [room?.status, currentPlayerId, turnEndsAtServer]);

    // GUESS timer
    useEffect(() => {
        if (!room?.status) return;

        if (room.status !== "GUESS") {
            setGuessText("");
            setGuessEndsAt(null);
            return;
        }

        setGuessEndsAt(guessEndsAtServer || null);
        // ✅ 자동 오픈 제거 (필요하면 "한번만" 로직으로 따로 제어) 
    }, [room?.status, guessEndsAtServer]);

    useEffect(() => {
        // GUESS 아니면 리셋
        if (room?.status !== "GUESS") {
            openedGuessOnceRef.current = false;
            return;
        }

        // 시간이 끝났거나 내가 추측자가 아니면 오픈 X
        if (!canGuess) return;
        if (guessRemainingSec === 0) return;

        // 이미 열었으면 또 열지 않기
        if (openedGuessOnceRef.current) return;
        openedGuessOnceRef.current = true;

        // ✅ 여기서 바로 모달 오픈
        setGuessOpen(true);
        setVoteResultDialogOpen(false);
        setVoteOpen(false);

        // (선택) 모바일이면 탭 이동 안 해도 되지만, 원하면 timeline 유지
        // if (isMobile) setMobileTab("timeline");
    }, [room?.status, canGuess, guessRemainingSec]);

    // ✅ 모바일: 게임 진행 상태로 들어가면 기본 탭을 타임라인으로
    useEffect(() => {
        if (!isMobile) return;
        if (["PLAYING", "VOTING", "GUESS", "REVEAL"].includes(room?.status)) {
            setMobileTab("timeline");
        }
    }, [isMobile, room?.status]);

    // ✅ 모바일: 내 차례가 되면 Say 모달 자동 오픈 (턴마다 1회)
    useEffect(() => {
        if (room?.status !== "PLAYING") {
            warnedTurnKeyRef.current = null;
            setTurnWarnOpen(false);
            return;
        }

        if (turnRemainingSec == null) return;

        // 턴을 구분하는 키 (라운드+현재 플레이어)
        const turnKey = `${room?.round ?? 0}-${currentPlayerId ?? "x"}`;

        // ✅ 3초 남았을 때 한 번만 경고
        if (turnRemainingSec === 3 && warnedTurnKeyRef.current !== turnKey) {
            warnedTurnKeyRef.current = turnKey;

            const who = playerNameMap.get(Number(currentPlayerId)) || `#${currentPlayerId}`;
            setTurnWarnMsg(`⏰ 3초 남음! (${who} 턴)`);
            setTurnWarnOpen(true);
        }

        // ✅ 턴이 바뀌면 다음 턴 경고 가능하게 리셋
        if (warnedTurnKeyRef.current && warnedTurnKeyRef.current !== turnKey) {
            // (사실상 위에서 새 turnKey 때 다시 세팅되지만 안전하게)
            // warnedTurnKeyRef.current = null; // 선택
        }
    }, [room?.status, room?.round, currentPlayerId, turnRemainingSec, playerNameMap]);


    // ─────────────────────────────────────────────
    // Say
    // ─────────────────────────────────────────────
    const [sayText, setSayText] = useState("");
    const turnTimeUp = room?.status === "PLAYING" && turnRemainingSec === 0;
    const canSay = joined && isMyTurn && !turnTimeUp && sayText.trim().length > 0;

    // ✅ 여기 추가
    useEffect(() => {
        if (!isMyTurn) setSayText("");
    }, [isMyTurn]);

    const handleSay = useCallback(async () => {
        const res = await postSay(sayText);

        // ✅ postSay가 boolean을 리턴해도 OK 처리
        const ok = res === true || res?.ok === true;

        if (ok) setSayText("");
        return ok;
    }, [postSay, sayText]);

    // ─────────────────────────────────────────────
    // Vote submit
    // ─────────────────────────────────────────────
    const handleVoteSubmit = useCallback(async () => {
        if (!voteTargetId) return;
        const res = await postVote(voteTargetId);
        const ok = res === true || res?.ok === true;
        if (!ok) return;

        setMyVotedTargetId(voteTargetId);
        setVoteOpen(false);
        setVoteTargetId(null);
    }, [postVote, voteTargetId]);

    // ─────────────────────────────────────────────
    // Guess submit
    // ─────────────────────────────────────────────
    const handleGuessSubmit = useCallback(async () => {
        const text = guessText.trim();
        if (!text) return;

        const res = await postGuess(text);

        const ok = res === true || res?.ok === true;
        const eMsg = res?.error;

        if (!ok) {
            setErrorSnackMsg(eMsg || "추측 실패");
            setErrorSnackOpen(true);
            return;
        }

        setGuessOpen(false);
        setGuessText("");
    }, [guessText, postGuess]);

    // ✅ 하단바 노출 조건 (최종 UX 룰)
    // - WAITING + joined + non-host : READY 바만
    // - WAITING + host              : (게임/호스트) 탭 + (참가/READY) 버튼 (타임라인 없음)
    // - InGame(PLAYING/VOTING/GUESS/REVEAL) : (게임/타임라인) 탭 (호스트 없음)

    const roomStatus = room?.status ?? "WAITING"; // ✅ 로딩 중엔 대기실로 취급
    const isWaiting = roomStatus === "WAITING";
    const isInGame = ["PLAYING", "VOTING", "GUESS", "REVEAL"].includes(roomStatus);

    // ✅ 방 상태가 WAITING 아니면 강퇴 모드 자동 해제(안전) 
    useEffect(() => {
        if (room?.status !== "WAITING") {
            setKickMode(false);
            setKickSelectedIds([]);
        }
    }, [room?.status]);

    // ✅ non-host는 WAITING이면 항상 하단바 노출 (미참가=참여버튼 / 참가후=준비버튼)
    const showReadyBar = isMobile && isWaiting && !isHost;

    // ✅ 여기서 joined 제거
    const showWaitingHostBar = isMobile && isWaiting && isHost;

    const showBottomTabs = isMobile && !showReadyBar && !showWaitingHostBar;

    // 탭 버튼 노출 여부
    const showGameTab = true;

    // ✅ 여기서 joined 제거 (탭 자체는 참가 전에도 보이게)
    const showHostTab = isWaiting && isHost;

    const showTimelineTab = isInGame || room?.status === "ENDED";

    // ✅ 모바일: 현재 탭이 노출 불가능해지면 자동 보정
    useEffect(() => {
        if (!isMobile) return;

        if (mobileTab === "host" && !showHostTab) setMobileTab("game");
        if (mobileTab === "timeline" && !showTimelineTab) setMobileTab("game");
    }, [isMobile, mobileTab, showHostTab, showTimelineTab]);

    // ─────────────────────────────────────────────
    // Timeline renderer
    // ─────────────────────────────────────────────
    const renderTimeline = useCallback(() => {
        return (
            <Box
                ref={timelineRef}
                sx={{
                    maxHeight: isMobile ? "70vh" : 380,
                    overflow: "auto",
                    pr: 0.5,
                }}
            >
                <Stack spacing={1.4}>
                    {saysGrouped.map(([r, items]) => (
                        <Box key={`r-${r}`}>
                            <Typography variant="caption" sx={{ fontWeight: 900, color: "text.secondary" }}>
                                라운드 {r}
                            </Typography>

                            <Stack spacing={1} sx={{ mt: 0.8 }}>
                                {items.map((s, idx) => {
                                    const displayName = s.nickname || playerNameMap.get(s.player_id) || `#${s.player_id}`;
                                    return (
                                        <Paper
                                            key={s.id || `${r}-${idx}`}
                                            variant="outlined"
                                            sx={{
                                                p: isMobile ? 1.6 : 1.2,
                                                borderRadius: isMobile ? 4 : 3,
                                                bgcolor: "background.paper",
                                            }}
                                        >
                                            <Typography
                                                variant={isMobile ? "body2" : "caption"}
                                                sx={{ color: "text.secondary", fontWeight: 900, lineHeight: 1.2 }}
                                            >
                                                {displayName} · {dayjs(s.created_at).format(isMobile ? "HH:mm" : "HH:mm:ss")}
                                            </Typography>
                                            <Typography
                                                variant={isMobile ? "body1" : "body2"}
                                                sx={{ whiteSpace: "pre-wrap", fontWeight: 800, lineHeight: 1.35, mt: 0.4 }}
                                            >
                                                {s.text}
                                            </Typography>
                                        </Paper>
                                    );
                                })}
                            </Stack>
                        </Box>
                    ))}

                    {safeSays.length === 0 && (
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            아직 발언이 없습니다.
                        </Typography>
                    )}
                </Stack>
            </Box>
        );
    }, [isMobile, saysGrouped, playerNameMap, safeSays.length]);

    const bottomBarBtnSx = {
        borderRadius: 3,
        fontWeight: 900,
        height: 44,
        flex: 1,
        minWidth: 0,
        px: 1,
        whiteSpace: "nowrap",
    };

    const headerBtnSx = {
        height: 40,
        minHeight: 40,
        borderRadius: 3,
        fontWeight: 900,
        px: { xs: 1.2, sm: 1.6 },
        minWidth: { xs: 76, sm: 92 },   // ✅ 좁을 때 폭 줄이기
        whiteSpace: "nowrap",
    };




    // ─────────────────────────────────────────────
    // ✅ 게임 진행 + 참가자 + READY 통합 렌더
    // ─────────────────────────────────────────────
    const renderGameplayContent = useCallback(() => {
        if (!room) {
            return (
                <Stack alignItems="center" sx={{ py: 5 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 1, color: "text.secondary" }}>로딩...</Typography>
                </Stack>
            );
        }

        const myName = me?.nickname || `#${myPlayerId}`;

        return (
            <Stack spacing={2} sx={{ mt: 2 }}>
                {/* ✅ 상태 바 (모바일에선 sticky 해제) */}
                <Paper
                    variant="outlined"
                    sx={{
                        p: isMobile ? 1.1 : 1.4,
                        position: isMobile ? "static" : "sticky",
                        top: isMobile ? "auto" : 12,
                        zIndex: isMobile ? "auto" : 5,
                        borderRadius: 3,
                        bgcolor: "background.paper",
                    }}
                >
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ sm: "center" }}
                        justifyContent="space-between"
                    >
                        <Typography sx={{ fontWeight: 900 }}>
                            {room?.status === "PLAYING"
                                ? isMyTurn
                                    ? "🟢 네 차례"
                                    : "🟡 대기 중"
                                : room?.status === "VOTING"
                                    ? "🟠 투표 진행 중"
                                    : room?.status === "GUESS"
                                        ? "🧠 마피아 추측 중"
                                        : room?.status === "REVEAL"
                                            ? "🟣 주제 확인 중"
                                            : room?.status === "WAITING"
                                                ? "🔵 대기실"
                                                : room?.status === "ENDED"
                                                    ? "✅ 종료"
                                                    : "…"}
                        </Typography>

                        <Stack direction="row" spacing={1} alignItems="center">
                            {room?.status === "PLAYING" &&
                                (turnRemainingSec != null ? (
                                    <Chip
                                        label={`⏱️ ${turnRemainingLabel}`}
                                        color={turnRemainingSec === 0 ? "default" : "success"}
                                        sx={{ fontWeight: 900 }}
                                    />
                                ) : (
                                    <Chip label="⏱️ ..." variant="outlined" sx={{ fontWeight: 900 }} />
                                ))}

                            {room?.status === "VOTING" && voteRemainingSec != null && (
                                <Chip
                                    label={`⏳ ${voteRemainingLabel}`}
                                    color={voteRemainingSec === 0 ? "default" : "warning"}
                                    sx={{ fontWeight: 900 }}
                                />
                            )}

                            {room?.status === "GUESS" && guessRemainingSec != null && (
                                <Chip
                                    label={`🧠 ${guessRemainingLabel}`}
                                    color={guessRemainingSec === 0 ? "default" : "secondary"}
                                    sx={{ fontWeight: 900 }}
                                />
                            )}
                        </Stack>
                    </Stack>
                </Paper>

                {/* ✅ 참가자 요약 + 내 READY + 내 주제 */}
                <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 4 }}>
                    <Stack spacing={1.4}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="subtitle1" sx={sectionTitleSx}>
                                    참가자 & 내 상태
                                </Typography>
                                <Chip size="small" variant="outlined" label={`${safePlayers.length}명`} />
                                {isHost && <Chip size="small" label="HOST" color="secondary" sx={{ fontWeight: 900 }} />}
                            </Stack>

                            {/* READY 버튼 (WAITING에서만) */}
                            {!isMobile && joined && (
                                <Button
                                    variant={myReady ? "contained" : "outlined"}
                                    onClick={postToggleReady}
                                    disabled={room?.status !== "WAITING"}
                                    sx={myReady ? mainActionBtnSx : subActionBtnSx}
                                >
                                    {myReady ? "준비완료" : "준비"}
                                </Button>
                            )}
                        </Stack>


                        {/* ✅ 참가자 칩(모바일 접기) */}
                        {isMobile ? (
                            <Accordion
                                defaultExpanded={true}
                                sx={{ borderRadius: 3 }}
                                disableGutters
                                elevation={0}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography sx={{ fontWeight: 900 }}>참가자 보기</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={1.2}>
                                        {/* ✅ 아코디언 안 강퇴 바 */}
                                        {canKick && (
                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                                <Button
                                                    variant={kickMode ? "contained" : "outlined"}
                                                    color="error"
                                                    onClick={() => {
                                                        setKickMode((v) => !v);
                                                        setKickSelectedIds([]);
                                                    }}
                                                    sx={{ borderRadius: 3, fontWeight: 900 }}
                                                    size="small"
                                                >
                                                    {kickMode ? "강퇴 모드 ON" : "강퇴 모드"}
                                                </Button>

                                                {kickMode && (
                                                    <>
                                                        <Button
                                                            variant="contained"
                                                            color="error"
                                                            disabled={kickSelectedIds.length === 0}
                                                            onClick={() => setKickConfirmOpen(true)}
                                                            sx={{ borderRadius: 3, fontWeight: 900 }}
                                                            size="small"
                                                        >
                                                            선택 강퇴 ({kickSelectedIds.length})
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            disabled={kickSelectedIds.length === 0}
                                                            onClick={() => setKickSelectedIds([])}
                                                            sx={{ borderRadius: 3, fontWeight: 900 }}
                                                            size="small"
                                                        >
                                                            선택 해제
                                                        </Button>
                                                    </>
                                                )}
                                            </Stack>
                                        )}

                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            {safePlayers.map((p) => {
                                                const name = p.nickname || p.username || `#${p.id}`;
                                                const isMe = p.id === myPlayerId;
                                                const showReady = room?.status === "WAITING";
                                                const isReady = !!p.is_ready;


                                                return (
                                                    <Chip
                                                        key={p.id}
                                                        label={isMe ? `${name} (나)` : name}
                                                        size="small"
                                                        color={showReady ? (isReady ? "success" : "default") : "default"}
                                                        variant={showReady ? (isReady ? "filled" : "outlined") : "outlined"}
                                                        onClick={
                                                            canKick && kickMode && !isMe
                                                                ? () => toggleKickSelect(p.id)
                                                                : undefined
                                                        }
                                                        clickable={canKick && kickMode && !isMe}
                                                        sx={{
                                                            fontWeight: 900,
                                                            opacity: showReady && !isReady ? 0.55 : 1,
                                                            borderWidth: kickSelectedIds.includes(Number(p.id)) ? 3 : (isMe ? 2 : 1),
                                                            borderStyle: "solid",
                                                            borderColor: kickSelectedIds.includes(Number(p.id)) ? "error.main" : "divider",
                                                            bgcolor: kickSelectedIds.includes(Number(p.id)) ? "error.lighter" : undefined,
                                                            cursor: canKick && kickMode && !isMe ? "pointer" : "default",
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Stack>
                                        {/* ✅ 강퇴 모드 안내 */}
                                        {canKick && kickMode && (
                                            <Alert severity="warning" sx={{ mt: 0.3 }}>
                                                강퇴할 대상을 선택한 뒤 <b>선택 강퇴</b>를 누르세요.
                                            </Alert>
                                        )}
                                    </Stack>



                                </AccordionDetails>
                            </Accordion>
                        ) : (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {safePlayers.map((p) => {
                                    const name = p.nickname || p.username || `#${p.id}`;
                                    const isMe = p.id === myPlayerId;
                                    // ✅ PC 브랜치에서도 READY 상태 변수 정의 (누락 버그 수정)
                                    const showReady = room?.status === "WAITING";
                                    const isReady = !!p.is_ready;


                                    const chipColor = showReady ? (isReady ? "success" : "default") : "default";
                                    const chipVariant = showReady ? (isReady ? "filled" : "outlined") : "outlined";

                                    return (
                                        <Chip
                                            key={p.id}
                                            label={isMe ? `${name} (나)` : name}
                                            size="small"
                                            color={chipColor}
                                            variant={chipVariant}
                                            sx={{
                                                fontWeight: 900,
                                                opacity: showReady && !isReady ? 0.55 : 1,
                                                borderWidth: isMe ? 2 : 1,
                                                borderStyle: "solid",
                                            }}
                                        />
                                    );
                                })}
                            </Stack>
                        )}

                        {/* 내 정보 */}
                        {!joined ? (
                            <Alert severity="info">참가하면 내 주제가 표시됩니다.</Alert>
                        ) : room?.status === "WAITING" ? (
                            <Alert severity="info">
                                <b>{myName}</b> · 대기 중
                            </Alert>
                        ) : (
                            <Alert severity="success">
                                <b>{myName}</b> · 내 주제: <b>{myTopic ?? "(로딩/미지급)"}</b>
                            </Alert>
                        )}

                        {/* 대기방 안내 */}
                        {room?.status === "WAITING" && joined && (
                            <Alert severity={allReady ? "success" : "warning"}>
                                {allReady
                                    ? isHost
                                        ? "👑 모두 준비 완료! 이제 ‘게임 시작’을 눌러주세요."
                                        : "✅ 모두 준비 완료! 호스트가 시작을 눌러주세요."
                                    : isHost ? (
                                        <>
                                            🔒 모두 준비완료 되면
                                            <br />
                                            시작할 수 있습니다.
                                        </>
                                    ) : (
                                        "🟡 준비 버튼을 눌러주세요."
                                    )}
                            </Alert>
                        )}
                    </Stack>
                </Paper >

                {/* ✅ 상태별 핵심 액션 */}
                {
                    room.status === "WAITING" && (
                        <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 4 }}>
                            <Stack spacing={1}>
                                <Typography sx={{ fontWeight: 900 }}>대기 중</Typography>
                                <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700 }}>
                                    모두 준비 완료되면 시작 가능해요.
                                </Typography>

                                {isHost ? (
                                    <Tooltip title={canStart ? "" : startDisabledReason} arrow>
                                        <span>
                                            <Button
                                                variant="contained"
                                                onClick={handleStartClick}
                                                disabled={!canStart}
                                                sx={mainActionBtnSx}
                                                fullWidth
                                            >
                                                게임 시작
                                            </Button>
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Alert severity="info">호스트가 게임을 시작하면 바로 진행됩니다.</Alert>
                                )}
                            </Stack>
                        </Paper>
                    )
                }

                {
                    room.status === "PLAYING" && (
                        <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 4 }}>
                            <Stack spacing={1.2}>
                                <Alert severity={isMyTurn ? "success" : "info"}>
                                    {isMyTurn
                                        ? "당신의 차례입니다."
                                        : "다른 사람 차례입니다. 타임라인을 확인해주세요."}
                                </Alert>

                                {/* ✅ PC에서는 기존 입력 유지 */}
                                {!isMobile && (
                                    <>
                                        <TextField
                                            value={sayText}
                                            onChange={(e) => setSayText(e.target.value)}
                                            placeholder="120자 이내"
                                            fullWidth
                                            disabled={!isMyTurn || turnTimeUp}
                                            inputProps={{ maxLength: 120 }}
                                        />

                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Button
                                                fullWidth={isMobile}
                                                variant="contained"
                                                onClick={handleSay}
                                                disabled={!canSay}
                                                sx={mainActionBtnSx}
                                            >
                                                제출
                                            </Button>

                                            <Chip label={isMyTurn ? "내 차례" : "대기"} variant="outlined" />
                                            <Typography variant="caption" sx={{ color: "text.secondary", ml: "auto" }}>
                                                {sayText.length}/120
                                            </Typography>
                                        </Stack>
                                    </>
                                )}

                                {/* ✅ 모바일에서는 입력 UI 제거 (모달에서만 입력) */}
                                {isMobile && isMyTurn && (
                                    <Button
                                        variant="contained"
                                        onClick={() => setSayDialogOpen(true)}
                                        sx={mainActionBtnSx}
                                        fullWidth
                                    >
                                        메시지 입력하기
                                    </Button>
                                )}
                            </Stack>
                        </Paper>
                    )
                }

                {
                    room.status === "GUESS" && (
                        <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 4 }}>
                            <Stack spacing={1.2}>
                                <Alert severity={canGuess ? "warning" : "info"}>
                                    {canGuess ? (
                                        <>
                                            제한 시간 안에 입력해주세요.
                                            {guessRemainingSec != null && (
                                                <>
                                                    <br />🧠 남은 시간: <b>{guessRemainingLabel}</b>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            🧠 마피아가 주제를 추측 중입니다.
                                            {guessRemainingSec != null && (
                                                <>
                                                    <br />🧠 남은 시간: <b>{guessRemainingLabel}</b>
                                                </>
                                            )}
                                        </>
                                    )}
                                </Alert>

                                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                                    <Button
                                        variant="contained"
                                        onClick={() => setGuessOpen(true)}
                                        disabled={!canGuess || guessRemainingSec === 0}
                                        sx={mainActionBtnSx}
                                        fullWidth={isMobile}
                                    >
                                        {canGuess ? "추측 입력" : "추측 대기"}
                                    </Button>

                                    {!isMobile && finalLoserPlayerId && (
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={`추측자: ${playerNameMap.get(Number(finalLoserPlayerId)) || `#${finalLoserPlayerId}`}`}
                                        />
                                    )}
                                </Stack>
                            </Stack>
                        </Paper>
                    )
                }

                {room.status === "ENDED" && <Alert severity="success">게임 종료!</Alert>}
            </Stack >
        );
    }, [
        room,
        isMobile,
        me,
        myPlayerId,
        myTopic,
        joined,
        isHost,
        allReady,
        myReady,
        safePlayers,
        postToggleReady,
        canStart,
        startDisabledReason,
        handleStartClick,
        isMyTurn,
        sayText,
        turnTimeUp,
        canSay,
        handleSay,
        turnRemainingSec,
        turnRemainingLabel,
        voteRemainingSec,
        voteRemainingLabel,
        playerNameMap,

        canGuess,
        guessRemainingSec,
        guessRemainingLabel,
        finalLoserPlayerId,
        canKick,
        kickMode,
        kickSelectedIds,
        toggleKickSelect,


    ]);

    const chipSx = {
        fontWeight: 900,
        height: 28,          // ✅ 높이 통일
        borderRadius: 999,
        "& .MuiChip-label": {
            px: 1.0,           // ✅ 좌우 패딩 통일
            lineHeight: 1,
        },
    };

    // ─────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────
    return (
        <Container
            maxWidth="md"
            sx={{
                py: 2.5,
                pb: isMobile ? ((showReadyBar || showWaitingHostBar) ? 12 : 9) : 4,
            }}
        >
            <Stack spacing={2.2}>
                {/* ✅ Header Card */}
                <Paper elevation={0} sx={cardSx}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ sm: "center" }}
                        spacing={2}
                    >
                        <Stack spacing={1}>
                            <Typography variant="h5" sx={{ fontWeight: 900 }}>
                                🎭 라이어 게임
                            </Typography>

                            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                                {/* ✅ PC에만 상태칩 */}
                                {!isMobile && (
                                    <Chip
                                        label={statusLabel}
                                        color={statusColor}
                                        size="small"
                                        sx={chipSx}
                                    />
                                )}

                                {/* ✅ ROUND/ROOM은 항상 */}
                                {room?.round != null && (
                                    <Chip label={`ROUND ${room.round}`} variant="outlined" size="small" sx={chipSx} />
                                )}

                                {room?.id && (
                                    <Chip label={`ROOM ${room.id}`} variant="outlined" size="small" sx={chipSx} />
                                )}

                                {/* ✅ PC에서만 TURN/HOST */}
                                {!isMobile && room?.status === "PLAYING" && currentPlayerId && (
                                    <Chip
                                        label={`TURN ${playerNameMap.get(Number(currentPlayerId)) || `#${currentPlayerId}`}`}
                                        color="primary"
                                        size="small"
                                        sx={chipSx}
                                    />
                                )}

                                {!isMobile && isHost && (
                                    <Chip label="HOST" color="secondary" size="small" sx={chipSx} />
                                )}
                            </Stack>
                        </Stack>

                        {/* 우측 컨트롤 */}
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ maxWidth: "100%" }}>

                            {/* ✅ 로비 버튼 */}
                            <Button
                                variant="outlined"
                                onClick={() => navigate("/main/games/topic-mafia")}
                                sx={headerBtnSx}
                            >
                                로비
                            </Button>

                            {/* ✅ 나머지 버튼들 */}
                            <Stack direction="row" spacing={1} alignItems="center">
                                {isHost && (
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() => setForceEndOpen(true)}
                                        disabled={room?.status === "ENDED"}
                                        sx={headerBtnSx}
                                    >
                                        강제 종료
                                    </Button>
                                )}

                                {!joined ? (
                                    <Button variant="contained" onClick={postJoin} sx={headerBtnSx}>
                                        참가
                                    </Button>
                                ) : (
                                    <Button
                                        variant="outlined"
                                        onClick={postLeave}
                                        disabled={room?.status !== "WAITING"}
                                        sx={headerBtnSx}
                                    >
                                        나가기
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                    </Stack>

                    {error && (
                        <Alert sx={{ mt: 2 }} severity="warning">
                            {error}
                        </Alert>
                    )}
                </Paper>

                {/* ✅ 모바일: 탭에 따라 한 섹션만 보여줌 / PC: 전부 보여줌 */}
                {(!isMobile || mobileTab === "game") && (
                    <Paper elevation={0} sx={cardSx}>
                        <Typography variant="h6" sx={sectionTitleSx}>
                            게임 진행
                        </Typography>
                        {renderGameplayContent()}
                    </Paper>
                )}

                {/* ✅ 타임라인 카드 (모바일: timeline 탭일 때만 / PC: 항상) */}
                {(!isMobile || mobileTab === "timeline") && (
                    <Paper elevation={0} sx={cardSx}>
                        <Stack spacing={1.4}>
                            <Typography variant="h6" sx={sectionTitleSx}>
                                타임라인
                            </Typography>

                            {/* ✅ 타임라인 상단: 내 주제 표시 */}
                            {joined && room?.status !== "WAITING" && (
                                <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 4 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Chip
                                            label="🧩 내 주제"
                                            color="success"
                                            size="small"
                                            sx={{ fontWeight: 900 }}
                                        />
                                        <Typography sx={{ fontWeight: 900 }}>
                                            {myTopic ?? "불러오는 중..."}
                                        </Typography>
                                    </Stack>
                                </Paper>
                            )}

                            {/* ✅ 지금 누구 차례인지 표시 */}
                            {room?.status === "PLAYING" && currentTurnName && (
                                <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 4 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Chip
                                            label="🎯 지금 차례"
                                            color={isMyTurn ? "success" : "info"}
                                            size="small"
                                            sx={{ fontWeight: 900 }}
                                        />
                                        <Typography sx={{ fontWeight: 900 }}>
                                            {currentTurnName}
                                            {isMyTurn ? " (나)" : ""}
                                        </Typography>

                                        {/* (선택) 남은시간도 같이 */}
                                        {turnRemainingSec != null && (
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={`⏱️ ${turnRemainingLabel}`}
                                                sx={{ fontWeight: 900 }}
                                            />
                                        )}
                                    </Stack>
                                </Paper>
                            )}

                            {/* ✅ 상대방 힌트 기다릴 때: 최근 라운드 최다 득표자 표시 */}
                            {room?.status === "PLAYING" && !isMyTurn && lastVoteSummary && (
                                <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 4 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Chip
                                            label="🗳️ 직전 라운드 최다 득표"
                                            color="warning"
                                            size="small"
                                            sx={{ fontWeight: 900 }}
                                        />

                                        {lastVoteSummary.noVotes ? (
                                            <Typography sx={{ fontWeight: 900 }}>없음 (무투표)</Typography>
                                        ) : (lastVoteSummary.topIds?.length > 0 ? (
                                            <Typography sx={{ fontWeight: 900 }}>
                                                {lastVoteSummary.isTie ? "동점: " : ""}
                                                {lastVoteSummary.topIds
                                                    .map((id) => playerNameMap.get(Number(id)) || `#${id}`)
                                                    .join(", ")}
                                                {lastVoteSummary.maxVotes != null ? ` (${lastVoteSummary.maxVotes}표)` : ""}
                                            </Typography>
                                        ) : (
                                            <Typography sx={{ fontWeight: 900 }}>정보 없음</Typography>
                                        ))}
                                    </Stack>
                                </Paper>
                            )}


                            {/* ✅ VOTING이면 타임라인 상단에 투표 버튼/안내 노출 */}
                            {room?.status === "VOTING" && (
                                <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 4 }}>
                                    <Stack spacing={1.2}>


                                        <Alert severity="warning">
                                            주제가 다른 사람을 투표!
                                            {voteRemainingSec != null && (
                                                <>
                                                    <br />⏳ 남은 시간: <b>{voteRemainingLabel}</b>
                                                </>
                                            )}

                                            {/* ✅ 5초 카운트다운 표시 */}
                                            {!voteDelayPassed && (
                                                <>
                                                    <br />🕒 투표는 <b>{voteDelayLeftSec}</b>초 후 시작됩니다.
                                                </>
                                            )}

                                            {myVotedTargetId && (
                                                <>
                                                    <br />✅ 투표 완료:{" "}
                                                    <b>{playerNameMap.get(myVotedTargetId) || `#${myVotedTargetId}`}</b>
                                                </>
                                            )}
                                        </Alert>

                                        {/* ✅ PC에서만 버튼 유지 / 모바일은 자동오픈이므로 버튼 없음 */}
                                        {(!isMobile || !voteRevealAt) && (   // ✅ 모바일인데 revealAt 없으면 버튼 보여줘
                                            <Button
                                                variant="contained"
                                                onClick={() => setVoteOpen(true)}
                                                disabled={!joined || !!myVotedTargetId || voteRemainingSec === 0 || (voteRevealAt ? !voteDelayPassed : false)}
                                                sx={mainActionBtnSx}
                                                fullWidth
                                            >
                                                {myVotedTargetId ? "투표 완료" : "투표하기"}
                                            </Button>
                                        )}

                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            {voteRemainingSec != null && (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={`남은시간 ${voteRemainingLabel}`}
                                                    sx={{ fontWeight: 900 }}
                                                />
                                            )}
                                            {myVotedTargetId && (
                                                <Chip
                                                    size="small"
                                                    color="success"
                                                    label={`완료: ${playerNameMap.get(myVotedTargetId) || `#${myVotedTargetId}`}`}
                                                    sx={{ fontWeight: 900 }}
                                                />
                                            )}
                                        </Stack>
                                    </Stack>
                                </Paper>
                            )}

                            {/* ✅ 실제 타임라인 */}
                            {renderTimeline()}
                        </Stack>
                    </Paper>
                )}





                {(!isMobile || mobileTab === "host") && room && (
                    <Paper elevation={0} sx={cardSx}>
                        {isHost && room?.status === "WAITING" ? (
                            <>
                                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: "background.paper" }}>
                                    <Stack spacing={1.5}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography sx={sectionTitleSx}>👑 호스트 패널</Typography>
                                            <Chip label="HOST ONLY" color="secondary" size="small" sx={{ fontWeight: 900 }} />
                                            {hostSettingsDirty && <Chip label="저장 필요" color="warning" size="small" sx={{ fontWeight: 900 }} />}
                                            {savingHostSettings && <Chip label="저장 중..." size="small" variant="outlined" />}
                                        </Stack>

                                        <Alert severity="info">
                                            주제 불러오기를 눌러 주제 목록을 불러온 뒤,
                                            <br />
                                            이번 게임에 사용할 주제를 골라주세요!
                                            <br />
                                            <br />
                                            인원/라운드/시간 설정은 아래 ‘고급 설정’에서 조정할 수 있어요.
                                        </Alert>

                                        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
                                            <TextField
                                                select
                                                label="주제 선택"
                                                value={hostSettings?.topic_category_id ?? ""}
                                                onChange={(e) => setHostSettings((p) => ({ ...(p || {}), topic_category_id: e.target.value }))}
                                                fullWidth
                                                disabled={room?.status !== "WAITING"}
                                                sx={controlFieldSx}
                                                helperText={(topicPairs || []).length === 0 ? "선택한 주제로 게임이 시작됩니다." : "선택한 주제로 시작(START) 요청에 포함됨"}
                                            >
                                                <MenuItem value="">(랜덤 / 미선택)</MenuItem>
                                                {(topicPairs || []).map((tp) => (
                                                    <MenuItem key={tp.id} value={tp.id}>
                                                        {tp.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>

                                            <Button
                                                variant="outlined"
                                                size="large"
                                                onClick={fetchTopicPairs}
                                                disabled={topicPairsLoading || room?.status !== "WAITING"}
                                                sx={{ ...controlBtnSx, minWidth: 150 }}
                                            >
                                                {topicPairsLoading ? "불러오는 중..." : "주제 불러오기"}
                                            </Button>
                                        </Stack>

                                        <Stack
                                            direction={{ xs: "column", sm: "row" }}
                                            spacing={1}
                                            alignItems={{ sm: "center" }}
                                            justifyContent="flex-end"
                                            sx={{ mt: 1.5 }}
                                        >
                                            {topicNotSelected && (
                                                <Typography variant="body2" sx={{ color: "warning.main", fontWeight: 900, mr: "auto" }}>
                                                    ⚠️ 주제를 선택해주세요. (미설정시 랜덤 시작)
                                                </Typography>
                                            )}

                                            <Tooltip title={canStart ? "" : startDisabledReason} arrow>
                                                <span>
                                                    <Button
                                                        variant="contained"
                                                        size="large"
                                                        onClick={handleStartClick}
                                                        disabled={!canStart}
                                                        sx={{ fontWeight: 900, borderRadius: 2, minWidth: 160 }}
                                                    >
                                                        게임 시작
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </Stack>
                                    </Stack>
                                </Paper>

                                <Divider sx={{ my: 2.5 }} />

                                <Accordion defaultExpanded={false} sx={{ borderRadius: 3, overflow: "hidden" }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography sx={{ fontWeight: 900 }}>⚙️ 고급 설정</Typography>
                                        </Stack>
                                    </AccordionSummary>

                                    <AccordionDetails>
                                        <Stack spacing={2}>
                                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                                <Typography variant="body2" sx={{ fontWeight: 900, color: "text.secondary" }}>
                                                    세부 설정(대기방에서만 변경 가능)
                                                </Typography>

                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    {hostSettingsDirty && <Chip size="small" color="warning" label="저장 필요" sx={{ fontWeight: 900 }} />}
                                                    {savingHostSettings && <Chip size="small" variant="outlined" label="저장 중..." />}
                                                </Stack>
                                            </Stack>

                                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                                <TextField
                                                    label="최대 인원"
                                                    type="number"
                                                    value={hostSettings?.max_players ?? 8}
                                                    onChange={(e) => setHostSettings((p) => ({ ...(p || {}), max_players: Number(e.target.value) }))}
                                                    fullWidth
                                                    sx={controlFieldSx}
                                                    inputProps={{ min: 3, max: 20 }}
                                                    disabled={room?.status !== "WAITING"}
                                                />

                                                <TextField
                                                    label="총 라운드"
                                                    type="number"
                                                    value={hostSettings?.total_rounds ?? 3}
                                                    onChange={(e) => setHostSettings((p) => ({ ...(p || {}), total_rounds: Number(e.target.value) }))}
                                                    fullWidth
                                                    sx={controlFieldSx}
                                                    inputProps={{ min: 1, max: 10 }}
                                                    disabled={room?.status !== "WAITING"}
                                                />
                                            </Stack>

                                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                                <TextField
                                                    label="마피아 수"
                                                    type="number"
                                                    value={hostSettings?.minority_count ?? 1}
                                                    onChange={(e) =>
                                                        setHostSettings((p) => ({
                                                            ...(p || {}),
                                                            minority_count: Number(e.target.value),
                                                        }))
                                                    }
                                                    fullWidth
                                                    sx={controlFieldSx}
                                                    inputProps={{ min: 1, max: 3 }}
                                                    disabled={room?.status !== "WAITING"}
                                                />

                                                <TextField
                                                    label="턴 제한(초)"
                                                    type="number"
                                                    value={hostSettings?.turn_time_limit_sec ?? 25}
                                                    onChange={(e) =>
                                                        setHostSettings((p) => ({
                                                            ...(p || {}),
                                                            turn_time_limit_sec: Number(e.target.value),
                                                        }))
                                                    }
                                                    fullWidth
                                                    sx={controlFieldSx}
                                                    inputProps={{ min: 10, max: 120 }}
                                                    disabled={room?.status !== "WAITING"}
                                                />
                                            </Stack>

                                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                                <TextField
                                                    label="투표 제한(초)"
                                                    type="number"
                                                    value={hostSettings?.vote_time_limit_sec ?? 30}
                                                    onChange={(e) =>
                                                        setHostSettings((p) => ({
                                                            ...(p || {}),
                                                            vote_time_limit_sec: Number(e.target.value),
                                                        }))
                                                    }
                                                    fullWidth
                                                    sx={controlFieldSx}
                                                    inputProps={{ min: 10, max: 120 }}
                                                    disabled={room?.status !== "WAITING"}
                                                />

                                                <TextField
                                                    select
                                                    label="주제 갱신 방식"
                                                    value={hostSettings?.topic_refresh_mode ?? "GAME"}
                                                    onChange={(e) =>
                                                        setHostSettings((p) => ({
                                                            ...(p || {}),
                                                            topic_refresh_mode: e.target.value,
                                                        }))
                                                    }
                                                    fullWidth
                                                    sx={controlFieldSx}
                                                    disabled={room?.status !== "WAITING"}
                                                    helperText="GAME: 게임 내 고정 / ROUND: 라운드마다 갱신"
                                                >
                                                    <MenuItem value="GAME">GAME</MenuItem>
                                                    <MenuItem value="ROUND">ROUND</MenuItem>
                                                </TextField>
                                            </Stack>

                                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
                                                <Button
                                                    variant="outlined"
                                                    disabled={room?.status !== "WAITING" || savingHostSettings}
                                                    onClick={() => {
                                                        if (!hostSettingsOrigRef.current) return;
                                                        try {
                                                            const orig = JSON.parse(hostSettingsOrigRef.current);
                                                            setHostSettings(orig);
                                                        } catch (e) { }
                                                    }}
                                                    sx={subActionBtnSx}
                                                >
                                                    변경 취소
                                                </Button>

                                                <Button
                                                    variant="contained"
                                                    onClick={handleSaveHostSettings}
                                                    disabled={room?.status !== "WAITING" || savingHostSettings || !hostSettingsDirty}
                                                    sx={mainActionBtnSx}
                                                >
                                                    {savingHostSettings ? "저장 중..." : "설정 저장"}
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                            </>
                        ) : (
                            <Alert severity="info">호스트 설정은 대기실(WAITING)에서만 표시됩니다.</Alert>
                        )}
                    </Paper>
                )}
            </Stack>

            {/* Vote Dialog */}
            <VoteDialog
                open={voteOpen}
                onClose={() => {
                    setVoteOpen(false);
                    setVoteTargetId(null);
                }}
                candidates={voteCandidates}
                value={voteTargetId}
                onChange={setVoteTargetId}
                disabledSubmit={!!myVotedTargetId || voteRemainingSec === 0 || voteDelayActive}
                disabledPick={!!myVotedTargetId || voteRemainingSec === 0 || voteDelayActive}
                onSubmit={handleVoteSubmit}
                playerNameMap={playerNameMap}
                recentSays={recentSaysForDialog}   // ✅ 추가
                isMobile={isMobile}               // ✅ 추가
                delayActive={voteDelayActive}
                delayLeftSec={voteDelayLeftSec}
            />

            {/* Guess Dialog */}
            <GuessDialog
                open={guessOpen}
                // ✅ 추측자면 닫기 방지 (ESC/바깥클릭/닫기버튼 모두 막는 쪽으로)
                preventClose={canGuess && guessRemainingSec !== 0}
                onClose={(event, reason) => {
                    // MUI reason: "backdropClick" | "escapeKeyDown"
                    if (canGuess && guessRemainingSec !== 0) return; // ✅ 추측자면 어떤 이유로든 닫지 않음
                    setGuessOpen(false);
                }}
                guessText={guessText}
                setGuessText={setGuessText}
                remainingLabel={guessRemainingLabel}
                disabledSubmit={!canGuess || guessRemainingSec === 0 || guessText.trim().length === 0}
                onSubmit={handleGuessSubmit}
            />

            {/* Force End Dialog */}
            <ForceEndDialog
                open={forceEndOpen}
                onClose={() => setForceEndOpen(false)}
                reason={forceEndReason}
                setReason={setForceEndReason}
                onConfirm={async () => {
                    const ok = await postForceEnd(forceEndReason);
                    if (ok) {
                        setForceEndOpen(false);
                        setForceEndReason("");
                    }
                }}
            />
            {/* ✅ Kick Confirm Dialog (멀티 선택 강퇴) */}
            <Dialog
                open={kickConfirmOpen}
                onClose={() => {
                    setKickConfirmOpen(false);

                    // ❗ 취소 시 강퇴 선택도 같이 정리하고 싶으면
                    setKickSelectedIds([]);
                    setKickMode(false);
                }}
            >
                <DialogTitle sx={{ fontWeight: 900 }}>강제 퇴장</DialogTitle>
                <DialogContent dividers>
                    <Typography sx={{ fontWeight: 800 }}>
                        다음 인원을 퇴장시킬까요?
                    </Typography>
                    <Typography sx={{ mt: 1, color: "text.secondary", fontWeight: 800 }}>
                        {selectedKickNames || "선택된 인원이 없습니다."}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setKickConfirmOpen(false);
                            setKickSelectedIds([]);
                            setKickMode(false);
                        }}
                        sx={subActionBtnSx}
                    >
                        취소
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={kickSelectedIds.length === 0}
                        onClick={async () => {
                            // ✅ 순차 처리(서버 부담/레이스 줄이기) 
                            for (const pid of kickSelectedIds) {
                                await postKick(pid);
                            }
                            setKickConfirmOpen(false);
                            setKickSelectedIds([]);
                            setKickMode(false);
                            await fetchState();
                        }}
                        sx={mainActionBtnSx}
                    >
                        퇴장
                    </Button>
                </DialogActions>
            </Dialog>


            {/* Confirm start (dirty settings) */}
            <Dialog
                open={confirmStartOpen}
                onClose={() => setConfirmStartOpen(false)}
                fullScreen={isMobile}
                fullWidth
                maxWidth={isMobile ? false : "xs"}
            >
                <DialogTitle sx={{ fontWeight: 900 }}>⚠️ 저장되지 않은 설정이 있습니다.</DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={1.2}>
                        <Alert severity="warning">
                            호스트 설정이 저장되지 않았습니다. <br />
                            지금 시작하면 변경사항이 반영되지 않을 수 있어요.
                        </Alert>

                        {hostSettingsDirty && (
                            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                • 변경된 설정을 저장한 뒤 시작할까요?
                            </Typography>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions sx={isMobile ? { position: "sticky", bottom: 0, bgcolor: "background.paper", zIndex: 1 } : undefined}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setConfirmStartOpen(false);
                            postStart();
                        }}
                        sx={subActionBtnSx}
                        fullWidth={isMobile}
                    >
                        저장 없이 시작
                    </Button>

                    <Button
                        variant="contained"
                        disabled={savingHostSettings}
                        onClick={async () => {
                            const res = await saveHostSettings();
                            const ok = res === true || res?.ok === true;
                            const eMsg = res?.error;

                            if (ok) {
                                setConfirmStartOpen(false);
                                await fetchState();
                                hostSettingsDirtyRef.current = false;
                                postStart();
                            } else {
                                setHostSaveSnackMsg(eMsg || "설정 저장 실패");
                                setHostSaveSnackOpen(true);
                            }
                        }}
                        sx={mainActionBtnSx}
                        fullWidth={isMobile}
                    >
                        저장 후 시작
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ✅ 모바일 Say Dialog */}
            <Dialog
                open={sayDialogOpen}
                onClose={(event, reason) => {
                    if (isMyTurn && (reason === "backdropClick" || reason === "escapeKeyDown")) return;
                    // 내 차례면 X 버튼/시스템 닫힘도 막고 싶으면 아래 유지
                    if (isMyTurn) return;
                    setSayDialogOpen(false);
                }}
                fullScreen={isMobile}
                fullWidth
                maxWidth={isMobile ? false : "sm"}
            >
                <DialogTitle sx={{ fontWeight: 900 }}>
                    {isMyTurn ? (
                        <>
                            🟢 당신의 차례
                            <Typography variant="body2" sx={{ mt: 0.6, fontWeight: 900, color: "text.secondary" }}>
                                🧩 내 주제: {myTopic ?? "불러오는 중..."}
                            </Typography>
                        </>
                    ) : (
                        "대기 중"
                    )}
                </DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={1.2}>
                        <Alert severity={isMyTurn ? "success" : "info"}>
                            {isMyTurn ? (
                                <>
                                    주제에 대한 힌트를 입력해주세요.
                                    <br />
                                    🧩 내 주제: <b>{myTopic ?? "(불러오는 중...)"}</b>

                                    {turnRemainingSec != null && (
                                        <>
                                            <br />⏱️ 남은 시간: <b>{turnRemainingLabel}</b>
                                        </>
                                    )}
                                </>
                            ) : (
                                "지금은 다른 사람 차례입니다."
                            )}
                        </Alert>

                        <TextField
                            value={sayText}
                            onChange={(e) => setSayText(e.target.value)}
                            placeholder="120자 이내"
                            fullWidth
                            disabled={!isMyTurn || turnTimeUp}
                            inputProps={{ maxLength: 120, enterKeyHint: "send" }}
                            autoFocus
                            multiline
                            minRows={3}
                            onKeyDown={async (e) => {
                                // IME 조합 중(한글 입력 중) Enter는 제출로 처리하면 안 됨
                                if (e.isComposing) return;

                                // ✅ Enter = 제출 (Shift+Enter는 줄바꿈)
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    if (!canSay) return;

                                    const ok = await handleSay();
                                    if (!ok) return;

                                    setSayDialogOpen(false);
                                    setSayCloseConfirmOpen(false);
                                    if (isMobile) setMobileTab("timeline");
                                    await fetchState();
                                }
                            }}
                        />

                        <Typography variant="caption" sx={{ color: "text.secondary", ml: "auto" }}>
                            {sayText.length}/120
                        </Typography>

                        {/* ✅ 여기부터 추가: 타임라인(최근 발언) */}
                        <Divider sx={{ my: 0.5 }} />

                        <Typography sx={{ fontWeight: 900 }}>
                            🧾 타임라인 (최근 발언)
                        </Typography>

                        <Box
                            sx={{
                                maxHeight: isMobile ? "32vh" : 220,
                                overflow: "auto",
                                pr: 0.5,
                                borderRadius: 2,
                            }}
                        >
                            <Stack spacing={1}>
                                {recentSaysForDialog.length === 0 ? (
                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                        아직 발언이 없습니다.
                                    </Typography>
                                ) : (
                                    recentSaysForDialog.map((s, idx) => {
                                        const displayName =
                                            s.nickname || playerNameMap.get(Number(s.player_id)) || `#${s.player_id}`;
                                        return (
                                            <Paper
                                                key={s.id || `dlg-say-${idx}`}
                                                variant="outlined"
                                                sx={{ p: 1.2, borderRadius: 2 }}
                                            >
                                                <Typography variant="caption" sx={{ fontWeight: 900, color: "text.secondary" }}>
                                                    {displayName} · {dayjs(s.created_at).format("HH:mm:ss")}
                                                </Typography>
                                                <Typography sx={{ fontWeight: 800, whiteSpace: "pre-wrap", mt: 0.3 }}>
                                                    {s.text}
                                                </Typography>
                                            </Paper>
                                        );
                                    })
                                )}
                            </Stack>
                        </Box>
                    </Stack>
                </DialogContent>

                <DialogActions
                    sx={
                        isMobile
                            ? { position: "sticky", bottom: 0, bgcolor: "background.paper", zIndex: 1 }
                            : undefined
                    }
                >
                    <Button
                        variant="outlined"
                        onClick={() => {
                            // ✅ 내 차례면 바로 닫지 말고 확인 모달 띄우기
                            if (isMyTurn) {
                                setSayCloseConfirmOpen(true);
                                return;
                            }
                            setSayDialogOpen(false);
                        }}
                        sx={subActionBtnSx}
                        fullWidth={isMobile}
                    >
                        닫기
                    </Button>

                    <Button
                        variant="contained"
                        onClick={async () => {
                            const ok = await handleSay();
                            if (!ok) return;

                            setSayDialogOpen(false);
                            setSayCloseConfirmOpen(false); // 혹시 열려있던 confirm도 닫기
                            if (isMobile) setMobileTab("timeline");
                            await fetchState();
                        }}
                        disabled={!canSay}
                        sx={mainActionBtnSx}
                        fullWidth={isMobile}
                    >
                        제출
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={sayCloseConfirmOpen}
                onClose={(e, reason) => {
                    // ✅ 여기서는 ESC/바깥클릭 막을지 말지 선택
                    // 난 UX상 여기서는 막아도 되고, 허용해도 되는데
                    // "확실히 의도"를 원하면 막는 게 더 일관됨.
                    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
                    setSayCloseConfirmOpen(false);
                }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle sx={{ fontWeight: 900 }}>⚠️ 닫으면 입력이 날아갈 수 있어요</DialogTitle>
                <DialogContent dividers>
                    <Typography sx={{ fontWeight: 800 }}>
                        시간 내에 제출 못 하면 무응답 처리될 수 있어요.
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                        그래도 닫을까요?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={() => setSayCloseConfirmOpen(false)}
                        sx={subActionBtnSx}
                    >
                        계속 입력
                    </Button>

                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => {
                            setSayCloseConfirmOpen(false);
                            setSayDialogOpen(false);
                        }}
                        sx={mainActionBtnSx}
                    >
                        닫기
                    </Button>
                </DialogActions>
            </Dialog>

            {/* vote_result Snackbar */}
            <Snackbar
                open={errorSnackOpen}
                onClose={() => setErrorSnackOpen(false)}
                autoHideDuration={2500}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                message={errorSnackMsg}
            />

            <Snackbar
                open={hostSaveSnackOpen}
                onClose={() => setHostSaveSnackOpen(false)}
                autoHideDuration={2500}
                anchorOrigin={isMobile ? { vertical: "top", horizontal: "center" } : { vertical: "bottom", horizontal: "center" }}
                TransitionComponent={(props) => <Slide {...props} direction={isMobile ? "down" : "up"} />}
                message={hostSaveSnackMsg}
            />

            <Snackbar
                open={turnWarnOpen}
                onClose={() => setTurnWarnOpen(false)}
                autoHideDuration={1200}
                anchorOrigin={isMobile ? { vertical: "top", horizontal: "center" } : { vertical: "bottom", horizontal: "center" }}
                TransitionComponent={(props) => <Slide {...props} direction={isMobile ? "down" : "up"} />}
                message={turnWarnMsg}
            />

            <Snackbar
                open={voteSnackOpen}
                onClose={() => setVoteSnackOpen(false)}
                autoHideDuration={2000}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                message={voteSnackMsg}
            />


            {/* vote_result Dialog */}
            <VoteResultDialog
                open={voteResultDialogOpen}
                onClose={() => setVoteResultDialogOpen(false)}
                payload={voteResultPayload}
                playerNameMap={playerNameMap}
            />

            <FinalResultDialog
                open={finalOpen && room?.status === "ENDED"}
                room={room}
                players={safePlayers}
                playerNameMap={playerNameMap}
                onGoWaiting={async () => {
                    const res = await postRematch();
                    const ok = res === true || res?.ok === true;
                    const eMsg = res?.error;
                    if (!ok) {
                        setErrorSnackMsg(eMsg || "다시하기 요청 실패");
                        setErrorSnackOpen(true);
                        return;
                    }

                    setFinalOpen(false);
                    setMobileTab("game");
                    setLastVoteSummary(null);
                    try { sessionStorage.removeItem(`tm_lastVote_${roomId}`); } catch (e) { }
                    setSays([]);
                    await fetchState();
                }}
                onGoLobby={() => navigate("/main/games/topic-mafia")}
            />





            {/* ✅ 모바일 하단 READY 바 (문구 간소화) */}
            {/* ✅ 모바일 하단 READY 바 (문구 제거 버전) */}
            {showReadyBar && (
                <Paper
                    elevation={10}
                    sx={{
                        position: "fixed",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1200,
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        p: 0.8,
                        bgcolor: "background.paper",
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* ✅ 미참가면 참여 버튼, 참가 후엔 준비/준비완료 */}
                        {!joined ? (
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={postJoin}
                                sx={bottomBarBtnSx}
                            >
                                참여
                            </Button>
                        ) : (
                            <Button
                                fullWidth
                                variant={myReady ? "contained" : "outlined"}
                                onClick={postToggleReady}
                                sx={bottomBarBtnSx}
                            >
                                {myReady ? "준비완료" : "준비"}
                            </Button>
                        )}
                    </Stack>
                </Paper>
            )}

            {/* ✅ 모바일: WAITING + HOST 바 (게임/호스트 탭 + READY 버튼, 타임라인 없음) */}
            {showWaitingHostBar && (
                <Paper
                    elevation={10}
                    sx={{
                        position: "fixed",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1200,
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        p: 0.8,
                        bgcolor: "background.paper",
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                            fullWidth
                            variant={mobileTab === "game" ? "contained" : "outlined"}
                            onClick={() => setMobileTab("game")}
                            sx={bottomBarBtnSx}
                        >
                            게임
                        </Button>

                        <Button
                            fullWidth
                            variant={mobileTab === "host" ? "contained" : "outlined"}
                            onClick={() => setMobileTab("host")}
                            sx={bottomBarBtnSx}
                        >
                            호스트
                        </Button>

                        {/* ✅ 여기만 변경 */}
                        {!joined ? (
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={postJoin}
                                sx={bottomBarBtnSx}
                            >
                                참여
                            </Button>
                        ) : (
                            <Button
                                fullWidth
                                variant={myReady ? "contained" : "outlined"}
                                onClick={postToggleReady}
                                sx={bottomBarBtnSx}
                            >
                                {myReady ? "준비완료" : "준비"}
                            </Button>
                        )}
                    </Stack>
                </Paper>
            )}

            {/* ✅ 모바일 하단 탭 바 (READY 바가 없을 때만) */}
            {/* ✅ 모바일 하단 탭 바 (READY / WAITING_HOST 바가 없을 때만) */}
            {showBottomTabs && (
                <Paper
                    elevation={10}
                    sx={{
                        position: "fixed",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1200,
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        p: 0.8,
                        bgcolor: "background.paper",
                    }}
                >
                    <Stack direction="row" spacing={1}>
                        {showGameTab && (
                            <Button
                                fullWidth
                                variant={mobileTab === "game" ? "contained" : "outlined"}
                                onClick={() => setMobileTab("game")}
                                sx={{ borderRadius: 3, fontWeight: 900, height: 44 }}
                            >
                                게임
                            </Button>
                        )}

                        {showTimelineTab && (
                            <Button
                                fullWidth
                                variant={mobileTab === "timeline" ? "contained" : "outlined"}
                                onClick={() => setMobileTab("timeline")}
                                sx={{ borderRadius: 3, fontWeight: 900, height: 44 }}
                            >
                                타임라인
                            </Button>
                        )}
                    </Stack>
                </Paper>
            )}
        </Container>
    );
}
